import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemButton,
  Checkbox,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import UndoIcon from '@mui/icons-material/Undo';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

const CATEGORY_CONFIG = {
  diagnoses: {
    Icon: MedicalServicesOutlinedIcon,
    color: '#c62828',
    getLabel: (item) => item.name || String(item.id || item._id),
    getSubtext: (item) =>
      [item.inheritanceMode, item.age ? `Age: ${item.age}` : null]
        .filter(Boolean)
        .join(' · '),
    getDate: (item) => item.dateReported || ''
  },
  phenotypes: {
    Icon: BiotechOutlinedIcon,
    color: '#1565c0',
    getLabel: (item) => item.name || String(item.id),
    getSubtext: () => '',
    getDate: (item) => item.dateRecorded || ''
  },
  genotypes: {
    Icon: ScienceOutlinedIcon,
    color: '#6a1b9a',
    getLabel: (item) => item.symbol || item.name || String(item.id),
    getSubtext: (item) =>
      [item.description, item.carrierStatus].filter(Boolean).join(' · '),
    getDate: (item) => item.dateRecorded || ''
  }
};

const getItemId = (item) =>
  item.id || (item._id ? String(item._id) : item.name);

// Human-readable labels for the auto-fillable field names. Covers every field
// listed in AUTO_FILLABLE_FIELDS on the backend (diagnoses + phenotypes +
// genotypes). New fields will fall back to a camelCase → "Camel Case" split
// via `humanizeFieldName` below.
const FIELD_LABELS = {
  age: 'Age',
  inheritanceMode: 'Inheritance Mode',
  dateReported: 'Date Reported',
  dateRecorded: 'Date Recorded',
  recordedAge: 'Recorded Age',
  clinicallyConfirmed: 'Clinically Confirmed',
  isPatientReported: 'Patient Reported',
  patientReported: 'Patient Reported',
  displayOnPedigree: 'Show on Pedigree',
  isPrivate: 'Private',
  carrierStatus: 'Carrier Status',
  comment: 'Comment',
  recordedBy: 'Recorded By',
  definition: 'Definition'
};

const humanizeFieldName = (field) => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
};

// Date-like fields whose value should be rendered via formatDate.
const DATE_FIELDS = new Set(['dateReported', 'dateRecorded']);

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function RecordRow({
  item,
  color,
  config,
  checkable,
  checked,
  onToggle,
  divider
}) {
  const id = getItemId(item);
  const label = config.getLabel(item);
  const subtext = config.getSubtext(item);
  const date = config.getDate(item);

  const avatar = (
    <ListItemAvatar sx={{ minWidth: 40 }}>
      <Avatar sx={{ width: 30, height: 30, bgcolor: `${color}1a`, color }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          {(label || '?').charAt(0).toUpperCase()}
        </span>
      </Avatar>
    </ListItemAvatar>
  );

  const content = (
    <Box
      sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, lineHeight: 1.35 }}
        noWrap
      >
        {label || '—'}
      </Typography>
      {subtext && (
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            mt: 0.15,
            lineHeight: 1.3
          }}
        >
          {subtext}
        </Typography>
      )}
      {date && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.25,
            lineHeight: 1.3
          }}
        >
          {formatDate(date)}
        </Typography>
      )}
    </Box>
  );

  const rowSx = {
    py: 2,
    px: 2,
    gap: 0.5,
    ...(divider && { borderBottom: '1px solid', borderColor: 'divider' })
  };

  if (!checkable) {
    return (
      <ListItem key={id} sx={rowSx}>
        {avatar}
        {content}
      </ListItem>
    );
  }

  return (
    <ListItemButton
      key={id}
      onClick={() => onToggle?.(id)}
      sx={{
        ...rowSx,
        backgroundColor: checked ? 'rgba(46, 125, 50, 0.08)' : 'inherit',
        '&:hover': {
          backgroundColor: checked
            ? 'rgba(46, 125, 50, 0.12)'
            : 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      {avatar}
      {content}
      <Checkbox
        checked={checked}
        size="small"
        color="success"
        edge="end"
        tabIndex={-1}
        sx={{ ml: 0.5 }}
      />
    </ListItemButton>
  );
}

// ---- Helper: Format field values for display ---------------------------------
const formatValue = (val) => {
  if (val === undefined || val === null || val === '') return '—';
  if (Array.isArray(val)) return val.length > 0 ? `[${val.length} items]` : '—';
  return String(val);
};

// Map plural category key → singular for the "Same X on both persons" subtitle.
const SINGULAR_CATEGORY = {
  diagnoses: 'diagnosis',
  phenotypes: 'phenotype',
  genotypes: 'genotype'
};

const isEmptyValue = (v) => v === undefined || v === null || v === '';

const fmtFieldValue = (field, value) => {
  if (isEmptyValue(value)) return '—';
  if (DATE_FIELDS.has(field)) return formatDate(value) || '—';
  return formatValue(value);
};

// ---- Matched record accordion -------------------------------------------------
// Visual vocabulary mirrors ClinicalRecordsStep's MatchingTestRow: a single
// 3-column Grid table (Field / Retained / Deleted) inside AccordionDetails,
// click-to-adopt deleted values, Undo to revert.
function MatchedRecordAccordion({
  categoryKey,
  matched,
  config,
  color,
  autoFilledKeys,
  onAutoFilledChange,
  conflictResolutions,
  onConflictResolutionChange
}) {
  const { retained, deleted, fieldDiff } = matched;
  const label = config.getLabel(retained);
  const singular = SINGULAR_CATEGORY[categoryKey] || categoryKey;

  // Build a flat list of (field, category) rows in display order.
  const allRows = [
    ...fieldDiff.autoFillable.map((f) => ({
      field: f,
      category: 'autoFillable'
    })),
    ...fieldDiff.conflict.map((f) => ({ field: f, category: 'conflict' })),
    ...fieldDiff.sameValue.map((f) => ({ field: f, category: 'sameValue' })),
    ...fieldDiff.retainedOnly.map((f) => ({
      field: f,
      category: 'retainedOnly'
    }))
  ];

  // A field is "adopted" when the deleted-side value is winning.
  const isAdopted = (field, category) => {
    const key = `${categoryKey}-${retained.id}-${field}`;
    if (category === 'autoFillable') return !!autoFilledKeys?.[key];
    if (category === 'conflict')
      return conflictResolutions?.[key] === 'deleted';
    return false;
  };

  const adoptedCount = allRows.filter(({ field, category }) =>
    isAdopted(field, category)
  ).length;

  const handleAdopt = (field, category) => {
    const key = `${categoryKey}-${retained.id}-${field}`;
    if (category === 'autoFillable') {
      onAutoFilledChange?.((prev) => ({ ...prev, [key]: true }));
    } else if (category === 'conflict') {
      onConflictResolutionChange?.((prev) => ({ ...prev, [key]: 'deleted' }));
    }
  };

  const handleRevert = (field, category) => {
    const key = `${categoryKey}-${retained.id}-${field}`;
    if (category === 'autoFillable') {
      onAutoFilledChange?.((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else if (category === 'conflict') {
      onConflictResolutionChange?.((prev) => ({ ...prev, [key]: 'retained' }));
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: 'none',
        backgroundColor: 'transparent'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          minHeight: 48,
          '& .MuiAccordionSummary-content': {
            my: 1,
            alignItems: 'center',
            gap: 1
          }
        }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: `${color}1a`,
            color,
            mr: 0.5
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 14 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.35 }}
            noWrap
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.3
            }}
          >
            Same {singular} on both persons
          </Typography>
        </Box>
        {adoptedCount > 0 && (
          <Chip
            label={`${adoptedCount} field(s) adopted`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Paper
          variant="outlined"
          sx={{ borderRadius: 1.5, overflow: 'hidden' }}
        >
          {/* Column headers */}
          <Grid
            container
            sx={{
              backgroundColor: '#fafafa',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1
            }}
          >
            <Grid size={3}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary'
                }}
              >
                Field
              </Typography>
            </Grid>
            <Grid size={4.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 13, color: 'success.main' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'success.dark'
                  }}
                >
                  Retained
                </Typography>
              </Box>
            </Grid>
            <Grid size={4.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 13, color: 'error.main' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'error.dark'
                  }}
                >
                  Deleted
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Field rows */}
          {allRows.map(({ field, category }, idx) => {
            const adopted = isAdopted(field, category);
            const deletedHasValue = !isEmptyValue(deleted[field]);
            const isInteractive =
              (category === 'autoFillable' || category === 'conflict') &&
              deletedHasValue;

            // Retained-column display value: shows deleted's value (in green)
            // when the user has adopted; otherwise the retained record's own
            // value (or empty for autoFillable).
            let retDisplay;
            if (adopted) {
              retDisplay = fmtFieldValue(field, deleted[field]);
            } else if (category === 'autoFillable') {
              retDisplay = '—';
            } else {
              retDisplay = fmtFieldValue(field, retained[field]);
            }
            const delDisplay = fmtFieldValue(field, deleted[field]);

            return (
              <Grid
                container
                key={field}
                sx={{
                  px: 2,
                  py: 1,
                  alignItems: 'center',
                  ...(idx < allRows.length - 1 && {
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }),
                  ...(adopted && {
                    backgroundColor: 'rgba(46, 125, 50, 0.05)'
                  })
                }}
              >
                <Grid size={3}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: 13
                    }}
                  >
                    {humanizeFieldName(field)}
                  </Typography>
                </Grid>
                <Grid size={4.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        ...(adopted && {
                          color: 'success.main',
                          fontWeight: 600
                        }),
                        ...(category === 'sameValue' && {
                          color: 'text.disabled'
                        })
                      }}
                    >
                      {retDisplay}
                    </Typography>
                    {adopted && (
                      <Tooltip
                        placement="right"
                        title="Revert to original retained value"
                      >
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => handleRevert(field, category)}
                        >
                          <UndoIcon
                            sx={{ fontSize: 14, color: 'warning.main' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid size={4.5}>
                  {isInteractive && !adopted ? (
                    <Tooltip
                      placement="right"
                      title="Click to adopt this value into retained"
                    >
                      <Typography
                        variant="body2"
                        onClick={() => handleAdopt(field, category)}
                        sx={{
                          fontSize: 13,
                          color: 'primary.main',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'inline-block',
                          borderBottom: '1px dashed',
                          borderColor: 'primary.light',
                          '&:hover': {
                            color: 'success.main',
                            borderColor: 'success.main'
                          }
                        }}
                      >
                        {delDisplay}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        ...(adopted && {
                          textDecoration: 'line-through',
                          color: 'text.disabled'
                        }),
                        ...(!adopted &&
                          category === 'sameValue' && {
                            color: 'text.disabled'
                          })
                      }}
                    >
                      {delDisplay}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

export default function GenomicCategoryStep({
  categoryKey,
  label,
  matched = [],
  retained = [],
  deleted = [],
  selected,
  onToggle,
  onSelectAll,
  autoFilledKeys = {},
  onAutoFilledChange,
  conflictResolutions = {},
  onConflictResolutionChange
}) {
  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.diagnoses;
  const { Icon, color } = config;
  const allSelected = deleted.length > 0 && selected.length === deleted.length;

  const hasAnyContent =
    matched.length > 0 || deleted.length > 0 || retained.length > 0;

  return (
    <Box>
      {deleted.length > 0 && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2
          }}
        >
          Retained person&apos;s {label.toLowerCase()} are preserved
          automatically. Tick the deleted person&apos;s records to transfer
          them; unticked records will be discarded.
        </Typography>
      )}
      {!hasAnyContent ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 4,
            textAlign: 'center'
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ color: 'success.main', fontSize: 28 }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'success.dark'
              }}
            >
              No conflicts to resolve
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.25
              }}
            >
              The deleted person has no {label.toLowerCase()} records. The
              retained person&apos;s records are preserved automatically.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          {/* Section header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
              gap: 1
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minWidth: 0
              }}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: `${color}1f`, color }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {retained.length} retained · {deleted.length} on deleted
                  {matched.length > 0 && ` · ${matched.length} matched by name`}
                </Typography>
              </Box>
            </Box>
            {deleted.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0
                }}
              >
                <Chip
                  label={`${selected.length} / ${deleted.length} to transfer`}
                  size="small"
                  color={selected.length > 0 ? 'success' : 'default'}
                  variant={selected.length > 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
                <Tooltip
                  placement="right"
                  title={
                    allSelected ? 'Clear all transfers' : 'Transfer all records'
                  }
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      allSelected ? <DeselectIcon /> : <SelectAllIcon />
                    }
                    onClick={() =>
                      allSelected
                        ? onSelectAll([])
                        : onSelectAll(deleted.map(getItemId))
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    {allSelected ? 'Clear' : 'Transfer All'}
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Retained-only records */}
          {retained.length > 0 && (
            <Box
              sx={{
                borderBottom:
                  deleted.length > 0 || matched.length > 0
                    ? '1px solid'
                    : 'none',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.85,
                  backgroundColor: 'rgba(46, 125, 50, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 15, color: 'text.secondary' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'success.dark'
                  }}
                >
                  Retained ({retained.length})
                </Typography>
              </Box>
              <List dense disablePadding>
                {retained.map((item, idx) => (
                  <RecordRow
                    key={getItemId(item)}
                    item={item}
                    color={color}
                    config={config}
                    checkable={false}
                    divider={idx < retained.length - 1}
                  />
                ))}
              </List>
            </Box>
          )}

          {/* Deleted-only records */}
          {deleted.length > 0 && (
            <Box
              sx={{
                borderBottom: matched.length > 0 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.85,
                  backgroundColor: 'rgba(211, 47, 47, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <DeleteOutlineIcon
                  sx={{ fontSize: 15, color: 'text.secondary' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'error.dark'
                  }}
                >
                  Deleted Only ({deleted.length})
                </Typography>
              </Box>
              <List dense disablePadding>
                {deleted.map((item, idx) => {
                  const id = getItemId(item);
                  return (
                    <RecordRow
                      key={id}
                      item={item}
                      color={color}
                      config={config}
                      checkable
                      checked={selected.includes(id)}
                      onToggle={onToggle}
                      divider={idx < deleted.length - 1}
                    />
                  );
                })}
              </List>
            </Box>
          )}

          {/* Matched-by-name records — same shell as ClinicalRecordsStep's
              "Matched by Name" section. Click deleted values inside an
              accordion to adopt them into the retained record. */}
          {matched.length > 0 && (
            <Box>
              <Box
                sx={{
                  px: 2,
                  py: 0.85,
                  backgroundColor: 'rgba(2, 136, 209, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CompareArrowsIcon
                  sx={{ fontSize: 15, color: 'text.secondary' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'info.dark'
                  }}
                >
                  Matched by Name ({matched.length})
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    ml: 0.5
                  }}
                >
                  — click deleted values to adopt into the retained record
                </Typography>
              </Box>
              <List dense disablePadding>
                {matched.map((pair, idx) => (
                  <ListItem
                    key={`${pair.retained.id}-${idx}`}
                    sx={{
                      p: 0,
                      display: 'block',
                      ...(idx < matched.length - 1 && {
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      })
                    }}
                  >
                    <MatchedRecordAccordion
                      categoryKey={categoryKey}
                      matched={pair}
                      config={config}
                      color={color}
                      autoFilledKeys={autoFilledKeys}
                      onAutoFilledChange={onAutoFilledChange}
                      conflictResolutions={conflictResolutions}
                      onConflictResolutionChange={onConflictResolutionChange}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
